import React from 'react';
import { Database, Cloud, RotateCw } from 'lucide-react';
import { useAppContext } from '../contexts/AppContext';

const DataSourceSwitcher: React.FC = () => {
    const { dataSourceMode, switchDataSource, refreshData, isLoading, currentUser } = useAppContext();

    const handleSwitch = (mode: 'firestore' | 'local') => {
        if (mode !== dataSourceMode) {
            switchDataSource(mode);
        }
    };

    // Only show data source switcher to admin and magnus users
    if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'magnus')) {
        return null;
    }

    return (
        <div className="flex items-center gap-2">
            {/* Data Source Label */}
            <span className="text-xs text-text-secondary font-medium">Data:</span>

            {/* Toggle Buttons */}
            <div className="flex bg-background border border-border rounded-lg p-0.5">
                {/* Local Mode Button */}
                <button
                    onClick={() => handleSwitch('local')}
                    disabled={isLoading}
                    className={`
                        flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all
                        ${dataSourceMode === 'local'
                            ? 'bg-primary text-white shadow-sm'
                            : 'text-text-secondary hover:text-text-primary hover:bg-background/50'
                        }
                        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                    title="Use local mock data (seed data with 17 iterations)"
                >
                    <Database size={14} />
                    <span>Local</span>
                </button>

                {/* Firestore Mode Button */}
                <button
                    onClick={() => handleSwitch('firestore')}
                    disabled={isLoading}
                    className={`
                        flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all
                        ${dataSourceMode === 'firestore'
                            ? 'bg-primary text-white shadow-sm'
                            : 'text-text-secondary hover:text-text-primary hover:bg-background/50'
                        }
                        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                    title="Use Firestore cloud database"
                >
                    <Cloud size={14} />
                    <span>Firestore</span>
                </button>
            </div>

            {/* Reload Button */}
            <button
                onClick={() => refreshData()}
                disabled={isLoading}
                className={`
                    p-1.5 rounded-lg border border-border hover:bg-background/50 transition-all
                    ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
                title="Reload data"
            >
                <RotateCw
                    size={14}
                    className={`text-text-secondary ${isLoading ? 'animate-spin' : ''}`}
                />
            </button>

            {/* Status Indicator */}
            {dataSourceMode === 'local' && (
                <div className="flex items-center gap-1 text-xs">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-text-secondary">Seed Data</span>
                </div>
            )}
        </div>
    );
};

export default DataSourceSwitcher;
